import { AppLayout, Box, Button, Checkbox, Flashbar, FlashbarProps, Header, SpaceBetween, Spinner, TextFilter } from '@amzn/awsui-components-core';
import ItemCard from '@amzn/awsui-components-core/item-card';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { appLayoutAriaLabels } from 'src/i18n-strings';
import { logger } from 'src/logger';
import { AppLogoService, SharedLogoItem } from 'src/services/AppLogoService';
import { AdminSideNavigationV2 } from '../AdminSideNavigationV2';
import LogoLibraryUploadModal from './LogoLibraryUploadModal';
import './LogoLibrary.scss';

interface LogoCardItem extends SharedLogoItem {
  previewUrl: string | null;
}

interface AppLogoCardItem {
  appId: string;
  appName: string;
  logoType: string;
  selectedLogoName: string | null;
  previewUrl: string | null;
}

export const LogoLibrary: React.FC = () => {
  const { parsedApplications } = useAppContext();
  const [logos, setLogos] = useState<LogoCardItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [filterText, setFilterText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // App logos (view only)
  const [appLogos, setAppLogos] = useState<AppLogoCardItem[]>([]);
  const [appLogosLoading, setAppLogosLoading] = useState(true);

  const displayFlashNotification = useCallback((content: string, type: FlashbarProps.Type) => {
    setFlashbarItems([
      {
        type,
        content,
        dismissible: true,
        dismissLabel: 'Dismiss message',
        onDismiss: () => setFlashbarItems([])
      }
    ]);
  }, []);

  const fetchLogos = useCallback(async () => {
    setIsLoading(true);
    try {
      const items = await AppLogoService.listSharedLogos();
      const cardItems: LogoCardItem[] = await Promise.all(
        items.map(async (item) => {
          const s3Location = AppLogoService.buildSharedLogoS3Location(item.filename);
          const previewUrl = await AppLogoService.getLogoPreviewUrl(s3Location);
          return { ...item, previewUrl };
        })
      );
      setLogos(cardItems);
    } catch (error: unknown) {
      logger.error('Failed to fetch shared logos', { error });
      displayFlashNotification('Failed to load shared logos.', 'error');
    }
    setIsLoading(false);
  }, [displayFlashNotification]);

  useEffect(() => {
    fetchLogos();
  }, [fetchLogos]);

  // Fetch app-specific logos for view section
  useEffect(() => {
    const appsWithLogos = parsedApplications.filter(
      (app) => app.logoConfig && app.logoConfig.logoType === 'app-specific' && app.logoConfig.s3Location
    );

    if (appsWithLogos.length === 0) {
      setAppLogos([]);
      setAppLogosLoading(false);
      return;
    }

    let cancelled = false;
    setAppLogosLoading(true);

    Promise.all(
      appsWithLogos.map(async (app) => {
        const previewUrl = await AppLogoService.getLogoPreviewUrl(app.logoConfig!.s3Location!);
        return {
          appId: app.id,
          appName: app.applicationName,
          logoType: app.logoConfig!.logoType,
          selectedLogoName: app.logoConfig!.selectedLogoName,
          previewUrl
        };
      })
    ).then((items) => {
      if (!cancelled) {
        setAppLogos(items);
        setAppLogosLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [parsedApplications]);

  // Determine which shared logos are in use by applications
  const sharedLogosInUse = useMemo(() => {
    const inUse = new Set<string>();
    parsedApplications.forEach((app) => {
      if (app.logoConfig?.logoType === 'shared-library' && app.logoConfig.selectedLogoName) {
        const filename = app.logoConfig.selectedLogoName.replace('shared:', '');
        inUse.add(`shared-logos/${filename}`);
      }
    });
    return inUse;
  }, [parsedApplications]);

  const handleDelete = async () => {
    if (selectedKeys.size === 0) return;
    setIsDeleting(true);
    try {
      const toDelete = logos.filter((l) => selectedKeys.has(l.key));
      await Promise.all(toDelete.map((item) => AppLogoService.deleteSharedLogo(item.key)));
      displayFlashNotification(`Deleted ${toDelete.length} logo(s).`, 'success');
      setSelectedKeys(new Set());
      fetchLogos();
    } catch (error: unknown) {
      logger.error('Failed to delete shared logos', { error });
      displayFlashNotification('Failed to delete logo(s).', 'error');
    }
    setIsDeleting(false);
  };

  const toggleSelection = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Only allow selecting logos not linked to any application
  const canDelete = useMemo(() => {
    if (selectedKeys.size === 0) return false;
    for (const key of selectedKeys) {
      if (sharedLogosInUse.has(key)) return false;
    }
    return true;
  }, [selectedKeys, sharedLogosInUse]);

  const filteredLogos = filterText ? logos.filter((l) => l.filename.toLowerCase().includes(filterText.toLowerCase())) : logos;

  return (
    <AppLayout
      ariaLabels={appLayoutAriaLabels}
      toolsHide
      navigation={<AdminSideNavigationV2 />}
      stickyNotifications
      notifications={<Flashbar items={flashbarItems} />}
      content={
        <SpaceBetween size="l">
          {/* Shared Logo Library */}
          <Header
            variant="h1"
            description="Upload and manage shared logos available across all applications."
            actions={
              <SpaceBetween size="xs" direction="horizontal">
                <Button onClick={fetchLogos} iconName="refresh" disabled={isLoading}>
                  Refresh
                </Button>
                <Button onClick={handleDelete} disabled={!canDelete} loading={isDeleting}>
                  Delete ({selectedKeys.size})
                </Button>
                <Button variant="primary" onClick={() => setShowUploadModal(true)}>
                  Upload Logo
                </Button>
              </SpaceBetween>
            }
            counter={`(${filteredLogos.length})`}
          >
            Shared Logo Library
          </Header>

          {showUploadModal && (
            <LogoLibraryUploadModal
              onDismiss={() => setShowUploadModal(false)}
              onSuccess={() => {
                setShowUploadModal(false);
                fetchLogos();
                displayFlashNotification('Logo uploaded successfully.', 'success');
              }}
            />
          )}

          <TextFilter
            filteringText={filterText}
            onChange={({ detail }) => setFilterText(detail.filteringText)}
            filteringPlaceholder="Find logos by name"
          />

          {isLoading ? (
            <Box textAlign="center" padding="xxl">
              <Spinner size="large" />
            </Box>
          ) : filteredLogos.length === 0 ? (
            <Box textAlign="center" color="inherit" padding="xxl">
              <SpaceBetween size="m">
                <b>No shared logos</b>
                <Box color="text-body-secondary">Upload logos to make them available across all applications.</Box>
                <Button onClick={() => setShowUploadModal(true)}>Upload Logo</Button>
              </SpaceBetween>
            </Box>
          ) : (
            <div className="logo-library__grid">
              {filteredLogos.map((item) => {
                const isInUse = sharedLogosInUse.has(item.key);
                return (
                  <ItemCard
                    key={item.key}
                    header={
                      <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                        <Checkbox checked={selectedKeys.has(item.key)} onChange={() => toggleSelection(item.key)} disabled={isInUse} />
                        <span className="logo-library__card-filename">{item.filename}</span>
                      </SpaceBetween>
                    }
                    footer={
                      <Box color="text-body-secondary" fontSize="body-s">
                        {item.size ? `${(item.size / 1024).toFixed(1)} KB` : ''}
                        {item.lastModified ? ` · ${new Date(item.lastModified).toLocaleDateString()}` : ''}
                        {isInUse && <span className="logo-library__in-use-badge"> · In use</span>}
                      </Box>
                    }
                  >
                    <div className="logo-library__card-preview">
                      {item.previewUrl ? <img src={item.previewUrl} alt={item.filename} /> : <Box color="text-status-inactive">No preview</Box>}
                    </div>
                  </ItemCard>
                );
              })}
            </div>
          )}

          {/* Application Logos (View Only) */}
          {(appLogosLoading || appLogos.length > 0) && (
            <>
              <Header variant="h2" description="Logos uploaded by individual applications. View only.">
                Application Logos
              </Header>

              {appLogosLoading ? (
                <Box textAlign="center" padding="l">
                  <Spinner />
                </Box>
              ) : (
                <div className="logo-library__grid">
                  {appLogos.map((item) => (
                    <ItemCard key={item.appId} header={<span className="logo-library__card-filename">{item.appName}</span>}>
                      <div className="logo-library__card-preview">
                        {item.previewUrl ? <img src={item.previewUrl} alt={item.appName} /> : <Box color="text-status-inactive">No preview</Box>}
                      </div>
                    </ItemCard>
                  ))}
                </div>
              )}
            </>
          )}
        </SpaceBetween>
      }
    />
  );
};
